import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Custom components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';

const RegisterPage: React.FC = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    agreeToTerms: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'agreeToTerms' ? checked : value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle registration logic here
    console.log('Registration attempted with:', formData);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 8,
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
          zIndex: 0,
        }}
      />
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Create Account
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Join The United Travel for premium transportation services
          </Typography>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <GridContainer spacing={2}>
              <GridItem xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  autoFocus
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12}>
                <TextField
                  fullWidth
                  id="company"
                  label="Company Name"
                  name="company"
                  autoComplete="organization"
                  value={formData.company}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      name="agreeToTerms" 
                      color="primary" 
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      required
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{' '}
                      <Link component={RouterLink} to="/terms">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link component={RouterLink} to="/privacy">
                        Privacy Policy
                      </Link>
                    </Typography>
                  }
                />
              </GridItem>
            </GridContainer>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={!formData.agreeToTerms}
            >
              Sign Up
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Box>
          </Box>
        </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default RegisterPage; 